/**
 * CompanyContextPanel — CCA-M2 AC-CCA-23
 *
 * Reusable read-only side panel that surfaces the company's guidance context
 * (principles/values/direction/market) at the point of authoring.
 *
 * Used in:
 *   - DecisionFormDialog (Decisions — AC-CCA-23)
 *   - OutcomeFormDialog (Outcomes — AC-CCA-11)
 *   - MeetingWizardPage (per agenda step — AC-CCA-20)
 *
 * Features:
 *   - Labeled layers (GOVERNANCE, COMPANY, DIRECTION, MARKET)
 *   - Collapsible per layer
 *   - Empty context → "Add your principles" nudge (not a blank box — AC-CCA-23)
 *   - Uses components/ui/* (no raw <button>/<input>)
 *   - Hydrates from GET /api/v1/alignment/context via useCompanyContext() hook
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, BookOpen, PlusCircle } from 'lucide-react';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { useCompanyContext } from '@/hooks/roros/alignment';
import {
  type GuidanceBundle,
  type GuidanceBundleEntry,
  type GuidanceLayer,
  GUIDANCE_LAYER_LABELS,
} from '@/services/roros/alignment.service';

// ─── Layer order ──────────────────────────────────────────────────────────────

const LAYER_ORDER: GuidanceLayer[] = ['GOVERNANCE', 'COMPANY', 'DIRECTION', 'MARKET'];

// ─── Layer section ────────────────────────────────────────────────────────────

interface LayerSectionProps {
  layer: GuidanceLayer;
  entries: GuidanceBundleEntry[];
}

function LayerSection({ layer, entries }: LayerSectionProps) {
  const [isOpen, setIsOpen] = useState(true);

  if (entries.length === 0) return null;

  const label = GUIDANCE_LAYER_LABELS[layer];

  return (
    <div className="border border-border rounded-md overflow-hidden">
      <Button
        variant="ghost"
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/50 rounded-none"
        onClick={() => setIsOpen((o) => !o)}
        aria-expanded={isOpen}
        aria-controls={`ccp-layer-${layer}`}
      >
        <span className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <span>{t(label)}</span>
        </span>
        <Badge variant="outline" className="text-xs">
          {entries.length}
        </Badge>
      </Button>
      {isOpen && (
        <div
          id={`ccp-layer-${layer}`}
          className="px-3 pb-3 pt-1 space-y-2 bg-muted/20"
        >
          {entries.map((entry) => (
            <div key={entry.id} className="text-xs">
              <span className="font-medium text-foreground">{entry.title}</span>
              {entry.description && (
                <p className="text-muted-foreground mt-0.5 leading-relaxed">
                  {entry.description}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyContextNudge() {
  return (
    <div
      className="flex flex-col items-center text-center gap-2 py-6 px-4"
      data-testid="ccp-empty-nudge"
    >
      <PlusCircle className="h-8 w-8 text-muted-foreground/50" />
      <p className="text-sm font-medium text-muted-foreground">
        {t('No company context yet')}
      </p>
      <p className="text-xs text-muted-foreground/70">
        {t('Add your principles, values, and direction in Settings to guide AI decisions.')}
      </p>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

interface CompanyContextPanelProps {
  /** Optional title override (defaults to "Company Context") */
  title?: string;
  /** When true, renders as a compact inline block (no border-box wrapper) */
  compact?: boolean;
  'data-testid'?: string;
}

/**
 * CompanyContextPanel — renders the company guidance context for authoring flows.
 *
 * AC-CCA-23: Empty context → shows "Add your principles" nudge (NOT a blank box).
 */
export function CompanyContextPanel({
  title,
  compact = false,
  'data-testid': testId,
}: CompanyContextPanelProps) {
  const { bundle, isLoading, isError } = useCompanyContext();

  const panelTitle = title ?? t('Company Context');

  // Group entries by layer
  const byLayer: Partial<Record<GuidanceLayer, GuidanceBundleEntry[]>> = {};
  for (const entry of bundle) {
    if (!byLayer[entry.layer]) byLayer[entry.layer] = [];
    byLayer[entry.layer]!.push(entry);
  }

  return (
    <div
      className={compact ? '' : 'border border-border rounded-lg overflow-hidden'}
      data-testid={testId ?? 'company-context-panel'}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b border-border">
        <BookOpen className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-foreground">{panelTitle}</h3>
      </div>

      {/* Body */}
      <div className="p-3 space-y-2">
        {isLoading && (
          <div className="flex items-center justify-center py-4" aria-label={t('Loading company context')}>
            <Spinner size="sm" />
          </div>
        )}

        {isError && (
          <p className="text-xs text-muted-foreground text-center py-2">
            {t('Could not load company context.')}
          </p>
        )}

        {!isLoading && !isError && bundle.length === 0 && (
          <EmptyContextNudge />
        )}

        {!isLoading && !isError && bundle.length > 0 && (
          <div className="space-y-2" role="region" aria-label={t('Company context layers')}>
            {LAYER_ORDER.map((layer) => {
              const entries = byLayer[layer] ?? [];
              return (
                <LayerSection key={layer} layer={layer} entries={entries} />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
